/**
 * Price utilities with industry-standard best practices
 * Provides type-safe, configurable, and performant price operations
 */

export interface ModifierOption {
  id?: string | number;
  name: string;
  price: string | number;
  isEnabled?: boolean;
}

export interface SelectedModifier {
  name: string;
  options: ModifierOption[];
}

/**
 * Safely parse a price (string | number) into a number
 * Handles edge cases like null, undefined, empty strings, and invalid formats
 * 
 * @param price - The price to parse (string, number, null, or undefined)
 * @returns Parsed number or 0 if invalid
 */
export const parsePrice = (price: string | number | null | undefined): number => {
  if (price == null) return 0;
  if (typeof price === "number") return isNaN(price) ? 0 : price;
  
  // Clean string: remove currency symbols, commas, and whitespace
  const cleaned = price.toString().replace(/[$,\s]/g, "").trim();
  if (cleaned === "") return 0;
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format a price with currency using Intl.NumberFormat
 * Configurable currency and locale for international support
 * 
 * @param price - The price to format
 * @param currency - Currency code (default: "USD")
 * @param locale - Locale string (default: "en-US")
 * @returns Formatted currency string
 */
export const formatPrice = (
  price: string | number,
  currency: string = "USD",
  locale: string = "en-US"
): string => {
  const numPrice = parsePrice(price);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numPrice);
};

/**
 * Calculate total price with base price, modifiers, and quantity
 * Optimized to parse prices once and use reduce for better performance
 * 
 * @param basePrice - Base item price
 * @param selectedModifiers - Array of selected modifiers with options
 * @param quantity - Quantity multiplier (default: 1)
 * @returns Total calculated price
 */
export const calculateTotalPrice = (
  basePrice: string | number,
  selectedModifiers: SelectedModifier[] = [],
  quantity: number = 1
): number => {
  const base = parsePrice(basePrice);

  // Calculate modifiers total using reduce for better performance
  const modifiersTotal = selectedModifiers.reduce((acc, modifier) => {
    const optionSum = modifier.options.reduce(
      (sum, option) => sum + parsePrice(option.price),
      0
    );
    return acc + optionSum;
  }, 0);

  return (base + modifiersTotal) * Math.max(1, quantity);
};

/**
 * Calculate price difference between two prices
 * Useful for showing savings or price changes
 * 
 * @param originalPrice - Original price
 * @param newPrice - New price
 * @returns Price difference (positive = increase, negative = decrease)
 */
export const calculatePriceDifference = (
  originalPrice: string | number,
  newPrice: string | number
): number => {
  return parsePrice(newPrice) - parsePrice(originalPrice);
};

/**
 * Calculate percentage discount
 * 
 * @param originalPrice - Original price
 * @param discountedPrice - Discounted price
 * @returns Discount percentage (0-100)
 */
export const calculateDiscountPercentage = (
  originalPrice: string | number,
  discountedPrice: string | number
): number => {
  const original = parsePrice(originalPrice);
  const discounted = parsePrice(discountedPrice);
  
  if (original === 0) return 0;
  
  const discount = ((original - discounted) / original) * 100;
  return Math.max(0, Math.round(discount * 100) / 100); // Round to 2 decimal places
};

/**
 * Apply tax to a price
 * 
 * @param price - Base price
 * @param taxRate - Tax rate as decimal (e.g., 0.08 for 8%)
 * @returns Price with tax applied
 */
export const applyTax = (
  price: string | number,
  taxRate: number
): number => {
  const basePrice = parsePrice(price);
  return basePrice * (1 + Math.max(0, taxRate));
};

/**
 * Round price to nearest cent
 * 
 * @param price - Price to round
 * @returns Price rounded to 2 decimal places
 */
export const roundPrice = (price: string | number): number => {
  const numPrice = parsePrice(price);
  return Math.round(numPrice * 100) / 100;
};
